import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import PaxCalculator from "@/components/PaxCalculator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ShieldCheck, BadgeCheck, MapPinned, ArrowRight } from "lucide-react";

export default function PaxPage() {
  const { t, lang } = useLang();
  const s = t.seo.pax;
  useEffect(() => {
    document.title = lang === "da"
      ? "PAX garderobe montering — fast pris | Belov Multiservice"
      : "PAX wardrobe assembly — fixed price | Belov Multiservice";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
    })();
    meta.content = lang === "da"
      ? "Få en fast pris på montering af din IKEA PAX-garderobe med det samme. Inkl. 25% moms. København og Nordsjælland."
      : "Get an instant fixed price for IKEA PAX wardrobe assembly. Incl. 25% VAT. Copenhagen and North Zealand.";
  }, [lang]);

  return (
    <div data-testid="pax-page">
      <section className="bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#1E3A2F] bg-white px-3 py-1.5 rounded-full border border-stone-200">
            {t.services.instant}
          </span>
          <h1 className="mt-4 text-3xl sm:text-5xl font-display font-extrabold tracking-tight text-stone-900 max-w-3xl">{t.pax.heroTitle}</h1>
          <p className="mt-4 text-lg text-stone-600 max-w-2xl">{t.pax.heroSubtitle}</p>
          <div className="mt-6 flex flex-wrap gap-4">
            {[[BadgeCheck, t.hero.point1], [ShieldCheck, t.hero.point3], [MapPinned, t.why.item4Title]].map(([Ic, tx], i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-stone-700 bg-white px-3 py-2 rounded-full border border-stone-200">
                <Ic className="w-4 h-4 text-emerald-600" /> {tx}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pax-calculator" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <PaxCalculator />
      </section>

      {/* SEO CONTENT */}
      <section className="bg-[#F4F1EA]" data-testid="pax-seo">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 space-y-14">
          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{s.introTitle}</h2>
            <p className="mt-4 text-stone-600 leading-relaxed">{s.introText}</p>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{s.whatTitle}</h2>
            <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
              {s.whatItems.map((it, i) => (
                <li key={i} className="flex items-center gap-2.5 text-stone-700">
                  <BadgeCheck className="w-5 h-5 text-emerald-600 shrink-0" /> <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{s.howTitle}</h2>
            <ol className="mt-6 grid sm:grid-cols-3 gap-5">
              {s.howSteps.map((st, i) => (
                <li key={i} className="bg-white border border-stone-200 rounded-2xl p-6">
                  <span className="font-mono text-2xl font-bold text-stone-200">0{i + 1}</span>
                  <p className="mt-2 text-sm text-stone-700 leading-relaxed">{st}</p>
                </li>
              ))}
            </ol>
            <a href="#pax-calculator" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1E3A2F] text-white px-7 py-3.5 text-base font-bold hover:bg-[#162D24] transition-colors" data-testid="pax-seo-cta">
              {s.cta} <ArrowRight className="w-5 h-5" />
            </a>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{s.faqTitle}</h2>
            <Accordion type="single" collapsible className="mt-6" data-testid="pax-faq">
              {s.faqs.map((f, i) => (
                <AccordionItem key={i} value={`pax-faq-${i}`} className="border-stone-200">
                  <AccordionTrigger className="text-left font-semibold text-stone-900 hover:no-underline" data-testid={`pax-faq-q-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-stone-600 leading-relaxed">
                    {f.a}
                    {i === s.faqs.length - 1 && (
                      <> <Link to="/serviceomraade" className="text-[#1E3A2F] font-semibold underline underline-offset-2">{s.areaLinkLabel}</Link>.</>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="flex flex-wrap gap-3" data-testid="pax-seo-links">
            <Link to="/serviceomraade" className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-50">{t.nav.area}</Link>
            <Link to="/oevrige-moebler" className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-50">{t.nav.other}</Link>
            <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-50">{t.footer.contactPage}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
